import {useState, useEffect} from 'react';
import {createGlobalStyle, css} from 'styled-components';
import styled from 'styled-components';

import {useIsCartoonito} from '../helpers';

export const sizes = {
    large: '992px',
    xlarge: '1312px',
    max: '1585px',
};

export const colors = {
    cartoonBlack: '#080008',
    gray: '#615C61',
    blue: '#009bdf',
    lightBlue: '#00aeef',
    yellow: '#ffea00',
    pink: '#ed0080',
    lightPink: '#f455a1',
};

export const breakpoints = {
    xs: 576,
    mobile: 600,
    sm: 768,
    md: 1024,
    lg: 1280,
    xl: 1440,
};

export var isMobile = window.innerWidth <= breakpoints.mobile;

export var isTablet = window.innerWidth <= breakpoints.md;

export const useIsMobile = () => {
    const isCartoonito = useIsCartoonito();
    const [isMobile, setIsMobile] = useState(
        isCartoonito
            ? window.innerWidth <= breakpoints.mobile ||
                  window.innerHeight <= breakpoints.mobile
            : window.innerWidth <= breakpoints.mobile,
    );

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, []);

    const handleWindowResize = () => {
        setIsMobile(
            isCartoonito
                ? window.innerWidth <= breakpoints.mobile ||
                      window.innerHeight <= breakpoints.mobile
                : window.innerWidth <= breakpoints.mobile,
        );
    };

    return isMobile;
};

export const useBreakpoint = () => {
    const [currentBreakpoint, setCurrentBreakpoint] = useState(null);
    const isCartoonito = useIsCartoonito();

    useEffect(() => {
        handleWindowResize();

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, []);

    const handleWindowResize = () => {
        const windowWidth = !isCartoonito
            ? window.innerWidth
            : Math.min(window.innerWidth, window.innerHeight);
        let lastBreakpoint = breakpoints.xl;

        Object.entries(breakpoints).forEach(point => {
            if (windowWidth <= point[1] && point[1] <= lastBreakpoint) {
                lastBreakpoint = point[1];
                setCurrentBreakpoint(point[0]);
            }
        });
    };

    return currentBreakpoint;
};

window.addEventListener('resize', () => {
    isMobile = window.innerWidth <= breakpoints.mobile;
    isTablet = window.innerWidth <= breakpoints.md;
});

// eslint-disable-next-line
export const notWebMediaQuery = `@media screen and (max-width: ${breakpoints.md}px)`;
// eslint-disable-next-line
export const webMediaQuery = `@media screen and (min-width: ${breakpoints.md +
    1}px)`;

// eslint-disable-next-line
export const tabletPortraitMediaQuery = `@media screen and (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.sm}px)`;
export const tabletMediaQuery = `@media screen and (max-width: ${
    breakpoints.md
}px) and (min-width: ${breakpoints.mobile + 1}px)`;
// eslint-disable-next-line
export const notTabletMediaQuery = `@media screen and (min-width: ${breakpoints.md +
    1}px)`;

// eslint-disable-next-line
export const mobileMediaQuery = `@media screen and (max-width: ${breakpoints.mobile}px)`;
// eslint-disable-next-line
export const notMobileMediaQuery = `@media screen and (min-width: ${breakpoints.mobile +
    1}px)`;

export const GlobalStyle = createGlobalStyle`
    * {
        font-family: "AvenirNextRoundedStd", "Arial";
        outline: none!important;
    }

    input:hover, button:hover, input:focus, button:focus {
        box-shadow: none !important;
        outline: none !important;
    }

    h1, h2, h3, h4, h5, h6 {
        font-family: "TTNormsProExtraBlack";
        line-height: 56px;
    }

    p {
        font-size: 18px;
        line-height: 30px;
    }

    body {
        margin: 0;
        padding: 0;
        position: relative;
        background-color: #000;
        color: #fff;

        ${tabletMediaQuery} {
            overflow-x: hidden;
        }

        ${notWebMediaQuery} {
            padding-bottom: 64px;
        }
    }

    button:hover, button:active, a:hover,
    a:active, svg:active, *::-moz-focus-inner {
        border: none;
        outline: none;
    }
`;

/* eslint-disable max-len */
export const CartoonitoLandscapeStyle = createGlobalStyle`
    @media screen and  (max-width: ${breakpoints.mobile}px) and (orientation: portrait) {
        html {
            transform: rotate(-90deg);
            transform-origin: left top;
            width: 100vh;
            height: 100vw;
            overflow-x: auto;
            position: absolute;
            top: 100%;
            left: 0;
        }
    }
`;
/* eslint-enable max-len */

export const PageBodyWrapper = styled.div`
    max-width: 1440px;
    margin-left: auto;
    margin-right: auto;

    ${({fluid}) =>
        fluid &&
        css`
            width: 100%;
        `}

    @media screen and (max-width: ${breakpoints.xl}px) {
        max-width: 100%;
    }
`;

export const Layout = styled.div`
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    box-sizing: border-box;

    @media (min-width: ${sizes.xlarge}) {
        padding-left: 2rem;
        padding-right: 2rem;
        max-width: 1280px;
    }
    @media (min-width: ${sizes.max}) {
        max-width: 1480px;
    }
    margin-left: auto;
    margin-right: auto;
`;

export const Row = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    ${props => props.margin && `margin-bottom: ${props.margin}`};
`;

export const Column = styled.div`
    display: flex;
`;

export const Flex = styled.div`
    display: ${({display}) => (display ? display : 'flex')};
    flex-wrap: ${({wrap}) => (wrap ? wrap : 'wrap')};
    flex-grow: ${({grow}) => (grow ? grow : 'initial')};
    align-items: ${({alignItems}) => (alignItems ? alignItems : 'center')};
    justify-content: ${({justifyContent}) =>
        justifyContent ? justifyContent : 'center'};
    ${({alignSelf}) => alignSelf && `align-self: ${alignSelf};`};
    ${({justifySelf}) => justifySelf && `justify-self: ${justifySelf};`};
    ${({background}) => background && `background: ${background}`};
    ${({color}) => color && `color: ${color}`};
    ${({border}) => border && `border: ${border}`};
    ${({boxShadow}) => boxShadow && `box-shadow: ${boxShadow}`};
    ${({margin}) => margin && `margin: ${margin}`};
    ${({padding}) => padding && `padding: ${padding}`};
    ${({width}) => width && `width: ${width}`};
    ${({height}) => height && `height: ${height}`};
    ${({minWidth}) => minWidth && `min-width: ${minWidth}`};
    ${({maxWidth}) => maxWidth && `max-width: ${maxWidth}`};
    ${({minHeight}) => minHeight && `min-height: ${minHeight}`};
    ${({maxHeight}) => maxHeight && `max-height: ${maxHeight}`};
    ${({position}) => position && `position: ${position}`};
    ${({zIndex}) => zIndex && `z-index: ${zIndex}`};
    ${({boxSizing}) => boxSizing && `box-sizing: ${boxSizing}`};
    ${({flex}) => flex && `flex: ${flex}`};
    ${({direction}) => direction && `flex-direction: ${direction}`};
    ${({order}) => order && `order: ${order}`};
    ${({overflow}) => overflow && `overflow: ${overflow}`};
    ${({transition}) => transition && `transition: ${transition}`};
    ${({onClick}) => onClick && 'cursor: pointer'};



    ${props =>
        props.alignLast &&
        `
        &::after {
            content: "";
            flex: auto;
        }
    `}

    ${props =>
        props.desktop &&
        `
        ${mobileMediaQuery} {
            display: none !important;
        }
        ${tabletMediaQuery} {
            display: none !important;
        }
    `}

    ${props =>
        props.tablet &&
        `
        ${notTabletMediaQuery} {
            display: none !important;
        }
    `}

    ${props =>
        props.mobile &&
        `
        ${notMobileMediaQuery} {
            display: none !important;
        }
    `}

    ${props =>
        props.tablet &&
        props.desktop &&
        `
        ${webMediaQuery} {
            display: flex !important;
        }
        ${tabletMediaQuery} {
            display: flex !important;
        }
    `}

`;
